import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { materialsApi, Material } from '../services/materialsApi';

// Define MaterialType locally if not exported from API, or import if available. 
// Assuming it matches the one in Materials.tsx
export enum MaterialType {
    Aluminum = 1,
    PVC = 2,
    Wood = 3,
    Steel = 4
}

interface MaterialEditDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    material: Material | null;
}

export const MaterialEditDialog: React.FC<MaterialEditDialogProps> = ({
    open,
    onClose,
    onSuccess,
    material
}) => {
    const [formData, setFormData] = useState<Partial<Material>>({
        name: '',
        code: '',
        materialType: MaterialType.Aluminum,
        description: '',
        unitPrice: 0,
        unit: 'm',
        stockQuantity: 0,
        isActive: true,
    });
    const [loading, setLoading] = useState(false);
    const { t } = useLanguage();
    

    useEffect(() => {
        if (material) {
            setFormData({ ...material });
        } else {
            setFormData({
                name: '',
                code: '',
                materialType: MaterialType.Aluminum,
                description: '',
                unitPrice: 0,
                unit: 'm',
                stockQuantity: 0,
                isActive: true,
            });
        }
    }, [material, open]);

    const handleSubmit = async () => {
        if (!formData.name) {
            blobticMessage.warning(t('common.messages.warning'), t('maker.msg.error.required'));
            return;
        }

        try {
            setLoading(true);
            if (material) {
                await materialsApi.update(material.id, formData);
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
            } else {
                await materialsApi.create(formData as Material);
                blobticMessage.success(t('common.messages.success'), t('maker.msg.success.create'));
            }
            onSuccess();
            onClose();
        } catch (err: any) {
            blobticMessage.error(t('common.messages.error'), t('maker.msg.failedSave', err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <RootDialogPage
            open={open}
            onClose={onClose}
            title={material ? t('maker.materials.edit') : t('maker.materials.add')}
            onAction={handleSubmit}
            actionLabel={material ? t('action.update') : t('action.create')}
            actionDisabled={loading}
            maxWidth="sm"
            fullWidth
        >
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label={t('maker.field.name')}
                    fullWidth
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <TextField
                    label={t('maker.field.code')}
                    fullWidth
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                />

                <FormControl fullWidth>
                    <InputLabel>{t('maker.field.materialType')}</InputLabel>
                    <Select
                        value={formData.materialType}
                        label={t('maker.field.materialType')}
                        onChange={(e) => setFormData({ ...formData, materialType: Number(e.target.value) })}
                    >
                        <MenuItem value={MaterialType.Aluminum}>{t('maker.materials.type.aluminum')}</MenuItem>
                        <MenuItem value={MaterialType.PVC}>{t('maker.materials.type.pvc')}</MenuItem>
                        <MenuItem value={MaterialType.Wood}>{t('maker.materials.type.wood')}</MenuItem>
                        <MenuItem value={MaterialType.Steel}>{t('maker.materials.type.steel')}</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label={t('maker.field.description')}
                    fullWidth
                    multiline
                    rows={2}
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label={t('maker.field.unitPrice')}
                        type="number"
                        fullWidth
                        required
                        value={formData.unitPrice}
                        onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) })}
                    />
                    <TextField
                        label={t('maker.field.unit')}
                        fullWidth
                        required
                        placeholder="m, pcs..."
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    />
                </Box>

                <TextField
                    label={t('maker.field.stockQuantity')}
                    type="number"
                    fullWidth
                    required
                    value={formData.stockQuantity}
                    onChange={(e) => setFormData({ ...formData, stockQuantity: parseFloat(e.target.value) })}
                />

                <FormControlLabel
                    control={<Checkbox checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />}
                    label={t('maker.field.active')}
                />
            </Box>
        </RootDialogPage>
    );
};
